/**
 * WordPress Dependencies
 */
import { select, subscribe, dispatch } from '@wordpress/data';

/**
 * Block duplication attribute copier - only triggered by custom duplicate section
 */

// Custom event name (imported from custom-duplicate-section.js)
const DUPLICATE_WITH_ATTRIBUTES_EVENT = 'appmaker-duplicate-with-attributes';

// Store for pending duplication data
let pendingDuplication = null;

// Listen for custom duplication events
window.addEventListener(DUPLICATE_WITH_ATTRIBUTES_EVENT, (event) => {
    const { originalClientId, originalAttributes, blockName, originalBlock } = event.detail;
    


    
    // Store the duplication data for when the new block is created
    pendingDuplication = {
        originalClientId,
        originalAttributes,
        blockName,
        originalBlock, // Complete block data including inner blocks
        timestamp: Date.now()
    };
    

});

// Store to track previous blocks state (including all inner blocks)
let previousAllBlocks = [];
let isInitialized = false;

/**
 * Recursively get all blocks including inner blocks
 */
function getAllBlocksRecursively(blocks = []) {
    const allBlocks = [];
    
    function extractBlocks(blockList) {
        blockList.forEach(block => {
            allBlocks.push({
                clientId: block.clientId,
                name: block.name,
                attributes: { ...block.attributes }
            });
            
            // Recursively process inner blocks
            if (block.innerBlocks && block.innerBlocks.length > 0) {
                extractBlocks(block.innerBlocks);
            }
        });
    }
    
    extractBlocks(blocks);
    return allBlocks;
}

// Subscribe to block editor data changes
const unsubscribe = subscribe(() => {
    const currentTopLevelBlocks = select('core/block-editor')?.getBlocks();
    
    if (!currentTopLevelBlocks) return;

    // Get all blocks including inner blocks
    const currentAllBlocks = getAllBlocksRecursively(currentTopLevelBlocks);

    // Initialize on first run
    if (!isInitialized) {
        previousAllBlocks = currentAllBlocks;
        isInitialized = true;
        return;
    }

    // Check if blocks were added (comparing total count including inner blocks)
    if (currentAllBlocks.length > previousAllBlocks.length) {
        // Only process if we have pending duplication data
        if (pendingDuplication) {
            // Find new blocks
            const previousIds = new Set(previousAllBlocks.map(b => b.clientId));
            const newBlocks = currentAllBlocks.filter(block => !previousIds.has(block.clientId));



            newBlocks.forEach(newBlock => {
                // Check if this new block matches the type we're expecting to duplicate
                if (newBlock.name === pendingDuplication.blockName) {

                    
                    // Use setTimeout to ensure inner blocks are fully initialized
                    setTimeout(() => {
                        copyAttributesToNewBlock(pendingDuplication, newBlock);
                    }, 100);
                    
                    // Clear pending duplication data after a delay
                    setTimeout(() => {
                        if (pendingDuplication) {

                            pendingDuplication = null;
                        }
                    }, 500);
                }
            });
        }
    }

    // Clean up old pending duplications (older than 5 seconds)
    if (pendingDuplication && (Date.now() - pendingDuplication.timestamp) > 5000) {

        pendingDuplication = null;
    }

    // Update our tracking with all blocks
    previousAllBlocks = currentAllBlocks;
});

/**
 * Copy attributes from original block to new block, including inner blocks
 */
function copyAttributesToNewBlock(originalData, newBlock) {
    try {

        
        // Use stored original block data if available, otherwise fetch from store
        const originalBlock = originalData.originalBlock || select('core/block-editor').getBlock(originalData.originalClientId);
        const newBlockData = select('core/block-editor').getBlock(newBlock.clientId);
        
        if (!originalBlock) {
            console.warn('Original block not found:', originalData.originalClientId);
            return;
        }
        
        if (!newBlockData) {
            console.warn('New block not found:', newBlock.clientId);
            return;
        }
        


        
        // Copy top-level attributes
        const currentNewBlockAttributes = select('core/block-editor').getBlockAttributes(newBlock.clientId);
        const mergedAttributes = {
            ...currentNewBlockAttributes,
            ...originalData.originalAttributes  // Original takes priority
        };
        

        
        // Update the new block's attributes
        dispatch('core/block-editor').updateBlockAttributes(newBlock.clientId, mergedAttributes);

        
        // For inner blocks, we need to get fresh data after the update
        setTimeout(() => {
            const updatedNewBlockData = select('core/block-editor').getBlock(newBlock.clientId);
            
            // Recursively copy inner block attributes
            if (originalBlock.innerBlocks && originalBlock.innerBlocks.length > 0) {

                copyInnerBlockAttributes(originalBlock.innerBlocks, updatedNewBlockData.innerBlocks);
            }
        }, 50);
        
    } catch (error) {
        console.error('Error copying block attributes:', error);
    }
}

/**
 * Recursively copy attributes from original inner blocks to new inner blocks
 */
function copyInnerBlockAttributes(originalInnerBlocks, newInnerBlocks) {
    if (!originalInnerBlocks || !newInnerBlocks) {

        return;
    }
    
    // Match blocks by index and block name
    const minLength = Math.min(originalInnerBlocks.length, newInnerBlocks.length);

    
    for (let i = 0; i < minLength; i++) {
        const originalInner = originalInnerBlocks[i];
        const newInner = newInnerBlocks[i];
        
        // Only copy if block names match
        if (originalInner.name === newInner.name) {
            try {
                // Copy attributes for this inner block
                const currentInnerAttributes = select('core/block-editor').getBlockAttributes(newInner.clientId);
                const mergedInnerAttributes = {
                    ...currentInnerAttributes,
                    ...originalInner.attributes  // Original takes priority
                };
                

                dispatch('core/block-editor').updateBlockAttributes(newInner.clientId, mergedInnerAttributes);
                
                // Recursively handle nested inner blocks
                if (originalInner.innerBlocks && newInner.innerBlocks) {
                    copyInnerBlockAttributes(originalInner.innerBlocks, newInner.innerBlocks);
                }
                
            } catch (error) {
                console.warn('Error copying inner block attributes:', error);
            }
        } else {
            console.warn(`Block name mismatch: ${originalInner.name} vs ${newInner.name}`);
        }
    }
} 